import React, { useEffect, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import SVG from 'react-inlinesvg';
import ChevronRightIcon from '../../../../../../assets/images/reskin-images/icon--chevrons-right.svg';
import {
  getSystemBanner,
  createSystemBanner,
  updateSystemBanner,
  BANNER_TYPES,
  APPLICATIONS,
} from '../../../api/systemBanner';
import FormLoader from '../../../components/FormLoader';
import useAlarm from '../../../context/AlarmContext';

const SystemBannerForm = ({ id, type, setOpenSideActionBoard, onSuccess }) => {
  const { setAlarm } = useAlarm();
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    banner_type: 'info',
    application: 'all',
    active_start: '',
    active_end: '',
  });

  const { data: bannerData, refetch } = useQuery(
    ['getSystemBanner', { id }],
    getSystemBanner,
    {
      enabled: type === 'edit' && !!id,
    }
  );

  useEffect(() => {
    if (type === 'edit' && id) {
      refetch();
    }
  }, [id, refetch, type]);

  useEffect(() => {
    if (bannerData?.system_banner) {
      const banner = bannerData.system_banner;
      setFormData({
        title: banner.title || '',
        message: banner.message || '',
        banner_type: banner.banner_type || 'info',
        application: banner.application || 'all',
        active_start: formatDateTimeForInput(banner.active_start),
        active_end: formatDateTimeForInput(banner.active_end),
      });
    }
  }, [bannerData]);

  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    // Format as YYYY-MM-DDTHH:MM for datetime-local input
    return date.toISOString().slice(0, 16);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const { mutate: createBannerMutation, isLoading: createLoading } =
    useMutation(createSystemBanner, {
      onSuccess: () => {
        setOpenSideActionBoard(null);
        setAlarm({ type: 'success', message: 'System banner created successfully' });
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        const errors = error?.response?.data?.errors;
        const errorMessage =
          errors?.length > 0 ? errors.join(', ') : error.message;
        setAlarm({ type: 'error', message: errorMessage });
      },
    });

  const { mutate: updateBannerMutation, isLoading: updateLoading } =
    useMutation(
      (data) => updateSystemBanner(id, data),
      {
        onSuccess: () => {
          setOpenSideActionBoard(null);
          setAlarm({ type: 'success', message: 'System banner updated successfully' });
          if (onSuccess) onSuccess();
        },
        onError: (error) => {
          const errors = error?.response?.data?.errors;
          const errorMessage =
            errors?.length > 0 ? errors.join(', ') : error.message;
          setAlarm({ type: 'error', message: errorMessage });
        },
      }
    );

  const handleSubmit = (e) => {
    e.preventDefault();

    const bannerPayload = {
      ...formData,
      active_start: new Date(formData.active_start).toISOString(),
      active_end: new Date(formData.active_end).toISOString(),
    };

    if (type === 'create') {
      createBannerMutation(bannerPayload);
    } else if (type === 'edit') {
      updateBannerMutation(bannerPayload);
    }
  };

  const isFormValid = () => {
    return (
      formData.title.trim() &&
      formData.message.trim() &&
      formData.active_start &&
      formData.active_end &&
      new Date(formData.active_end) > new Date(formData.active_start)
    );
  };

  const label = {
    edit: 'Edit System Banner',
    create: 'Create System Banner',
  };

  return (
    <>
      <div className="flex justify-between items-center relative pb-4 px-8">
        <button
          className="p-2 flex items-center -ml-2"
          type="button"
          onClick={() => setOpenSideActionBoard(null)}
        >
          <SVG src={ChevronRightIcon} />
        </button>
        <span
          style={{ width: 'calc(100% + 6rem)' }}
          className="absolute pin-b pin-r -mx-16 h-px bg-gray flex"
        />
      </div>

      <form
        className="overflow-y-scroll h-full px-8 py-8"
        onSubmit={handleSubmit}
      >
        <h1 className="text-2xl font-bold mb-8">{label[type]}</h1>

        <div style={{ gap: '16px' }} className="flex flex-col">
          {/* Title */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              TITLE *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Scheduled Maintenance"
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              maxLength={255}
              required
            />
          </div>

          {/* Message */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              MESSAGE *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Enter the message to display to users..."
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              rows="4"
              required
            />
          </div>

          {/* Banner Type */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              BANNER TYPE *
            </label>
            <select
              name="banner_type"
              value={formData.banner_type}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
            >
              {BANNER_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Application */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              APPLICATION *
            </label>
            <select
              name="application"
              value={formData.application}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
            >
              {APPLICATIONS.map((app) => (
                <option key={app.value} value={app.value}>
                  {app.label}
                </option>
              ))}
            </select>
          </div>

          {/* Active Start */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              START DATE/TIME *
            </label>
            <input
              type="datetime-local"
              name="active_start"
              value={formData.active_start}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              required
            />
          </div>

          {/* Active End */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              END DATE/TIME *
            </label>
            <input
              type="datetime-local"
              name="active_end"
              value={formData.active_end}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-purple-600"
              required
            />
            {formData.active_start &&
              formData.active_end &&
              new Date(formData.active_end) <= new Date(formData.active_start) && (
                <p className="text-red-500 text-sm mt-1">
                  End date must be after start date
                </p>
              )}
          </div>

          {/* Preview */}
          {formData.title && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                PREVIEW
              </label>
              <div
                className={`p-4 rounded-lg border ${
                  formData.banner_type === 'error'
                    ? 'bg-red-50 border-red-200 text-red-800'
                    : formData.banner_type === 'warning'
                    ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                    : formData.banner_type === 'maintenance'
                    ? 'bg-orange-50 border-orange-200 text-orange-800'
                    : 'bg-blue-50 border-blue-200 text-blue-800'
                }`}
              >
                <strong>{formData.title}</strong>
                {formData.message && (
                  <p className="mt-1 text-sm">{formData.message}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          className="mt-8 mb-20 p-4 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          type="submit"
          disabled={!isFormValid()}
        >
          {type === 'create' ? 'Create Banner' : 'Update Banner'}
        </button>

        {(createLoading || updateLoading) && <FormLoader />}
      </form>
    </>
  );
};

export default SystemBannerForm;

